<template>
  <div class="action-menu">
    <div class="action-icons" :class="{ extended }">
      <div class="inner-wrapper">
        <div class="icon edit">
          <font-awesome-icon :icon="faPenAlt" @click="() => edit()" />
        </div>
        <div class="icon remove" v-if="isHeadAccountant" @click="() => remove()">
          <font-awesome-icon :icon="faTrashAlt" />
        </div>
        <div class="icon approve" v-if="isHeadAccountant && (item.status === CASH_LOG_STATUSES.PENDING)" @click="() => approve()">
          <font-awesome-icon :icon="faCheckCircle" />
        </div>
        <div class="close" v-if="isDesktop" @click="() => hide()">
          <div class="line line-1"></div>
          <div class="line line-2"></div>
        </div>
      </div>
    </div>
    <div class="more-icon" @click="() => show()">
      <font-awesome-icon :icon="faEllipsisV" />
    </div>
  </div>
</template>

<script>
import { computed, onMounted, ref, watch } from '@vue/composition-api';
import { createNamespacedHelpers } from 'vuex-composition-helpers';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { faEllipsisV, faPenAlt, faTrashAlt, faCheckCircle } from '@fortawesome/free-solid-svg-icons';

import { PERMISSIONS, CASH_LOG_STATUSES, ERROR_CODES } from '@/consts';
import { hasPermissions } from '@/helpers/permission';

import cashLogService from '@/views/CashLog/CashLogService';

const { useActions } = createNamespacedHelpers('cashLog');
const { useActions: useFlashActions } = createNamespacedHelpers('flash');
const { useState: useAppState } = createNamespacedHelpers('app');

export default {
  components: {
    FontAwesomeIcon
  },
  props: {
    item: {
      type: Object,
      required: true
    }
  },
  setup(props, { emit }) {
    const { isDesktop } = useAppState(['isDesktop']);

    const { setTargetingItem } = useActions(['setTargetingItem']);
    const { error } = useFlashActions(['error']);

    const extended = ref(false);

    const show = () => {
      extended.value = true;
    };

    const hide = () => {
      extended.value = false;
    };

    const edit = () => {
      setTargetingItem({ item: props.item });
      if (isDesktop.value) {
        hide();
      }
    };

    const remove = async () => {
      try {
        await cashLogService.updateCashLogStatus(props.item.id, CASH_LOG_STATUSES.RETRACTED);
        emit('action-made');
      } catch(err) {
        const { code, message } = err;
        const cashLogErrorCodes = [
          ERROR_CODES.CASH_LOG_CANNOT_REMOVE,
        ];
        const i = cashLogErrorCodes.findIndex((c) => c === code);

        if (i >= 0) {
          error('CASH_LOG_CANNOT_REMOVE');
        } else {
          error(message);
        }
      }
    };

    const approve = async () => {
      await cashLogService.updateCashLogStatus(props.item.id, CASH_LOG_STATUSES.REGISTERED);
      emit('action-made');
    };

    const isHeadAccountant = computed(() => hasPermissions([PERMISSIONS.HEAD_ACCOUNTANT_ACCESS]));

    watch(isDesktop, (newVal) => {
      if (newVal) extended.value = false;
      else extended.value = true;
    });

    onMounted(() => {
      if (!isDesktop.value) extended.value = true;
    });

    return {
      faEllipsisV,
      faPenAlt,
      faTrashAlt,
      faCheckCircle,
      extended,
      hide,
      show,
      isHeadAccountant,
      edit,
      remove,
      approve,
      CASH_LOG_STATUSES,
      isDesktop
    };
  }
};
</script>

<style lang="scss">
@import "~@/assets/scss/colors";

.action-menu {
  position: relative;

  .more-icon {
    cursor: pointer;
    transition: all .2s ease-in-out;
    color: $independence;

    &:hover {
      color: $geranium;
    }
  }

  .action-icons {
    position: absolute;
    top: 50%;
    right: 0;
    transform: translateY(-50%);
    width: 0;
    height: 42px;
    text-align: right;
    background-color: $babyPowder;
    transition: all .2s ease-in-out;
    overflow: hidden;

    .inner-wrapper {
      width: 170px;
      padding: 5px;
    }

    &.extended {
      width: 170px;
    }

    .icon {
      display: inline-block;
      width: 32px;
      height: 32px;
      text-align: center;
      margin-right: 7px;
      position: relative;
      cursor: pointer;
      transition: all .2s ease-in-out;

      &::before {
        content: "";
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background-color: $independence;
        border-radius: 50%;
        opacity: .2;
        transition: all .2s ease-in-out;
      }

      svg {
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translateX(-50%) translateY(-50%);
      }

      &:hover {
        &.edit {
          color: $blueNcs;

          &::before {
            background-color: $blueNcs;
          }
        }

        &.remove {
          color: $paradisePink;

          &::before {
            background-color: $paradisePink;
          }
        }

        &.approve {
          color: $shamrockGreen;

          &::before {
            background-color: $shamrockGreen;
          }
        }
      }
    }

    .close {
      margin-left: 15px;
      display: inline-block;
      position: relative;
      top: 4px;
      width: 21px;
      height: 21px;
      cursor: pointer;
      opacity: 1;

      .line {
          height: 1px;
          width: 21px;
          background-color: $independence;
          transform-origin: center;
          margin: 0 auto;
          transition: all .2s ease-in-out;
      }

      .line-1 {
          transform: translateY(11px) rotate(45deg);
      }

      .line-2 {
          transform: translateY(10px) rotate(135deg);
      }

      &:hover {
        .line {
          background-color: $geranium;
        }
      }
    }
  }

  @media (max-width: 991px) {
    .action-icons {

      &.extended {
        width: 100% !important;
      }
    }

    .inner-wrapper {
      text-align: start;
    }
  }
}
</style>
